import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { 
  Fish,
  CalendarDots,
  ChartLine,
  FishSimple,
  Images,
  SignOut,
  House,
  TrendUp,
  Users,
  CurrencyEur,
  Prohibit,
  Receipt,
  List,
  X
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAnalyticsPage = () => {
  const { token, logout } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      let url = `${API}/admin/analytics`;
      if (period !== 'all') {
        const now = new Date();
        let startDate;
        if (period === 'month') {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        } else if (period === 'year') {
          startDate = new Date(now.getFullYear(), 0, 1);
        }
        if (startDate) {
          url += `?start_date=${startDate.toISOString().split('T')[0]}`;
        }
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const navItems = [
    { path: '/admin/dashboard', icon: CalendarDots, label: 'Réservations' },
    { path: '/admin/invoices', icon: Receipt, label: 'Factures' },
    { path: '/admin/trips', icon: FishSimple, label: 'Sorties Pêche' },
    { path: '/admin/gallery', icon: Images, label: 'Galerie' },
    { path: '/admin/analytics', icon: ChartLine, label: 'Analytics' },
  ];

  const COLORS = ['#b7ede0', '#000000', '#10B981', '#8B5CF6', '#FF3B30'];

  // Prepare chart data
  const tripChartData = analytics?.bookings_by_trip
    ? Object.entries(analytics.bookings_by_trip).map(([id, data]) => ({
        name: data.name.substring(0, 15) + (data.name.length > 15 ? '...' : ''),
        reservations: data.count,
        revenue: data.revenue
      }))
    : [];

  const monthlyChartData = analytics?.bookings_by_month
    ? Object.entries(analytics.bookings_by_month)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, data]) => ({
          month: month,
          reservations: data.count,
          revenue: data.revenue
        }))
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-analytics-page">
      {/* Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-[#000000] text-white">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png" alt="Calvi Fishing" className="h-10 w-10 object-contain rounded-full" />
            <span className="text-lg font-bold font-['Playfair_Display']">CALVI FISHING</span>
          </Link>
        </div>
        
        <nav className="mt-6 px-3">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                item.path === '/admin/analytics'
                  ? 'bg-[#b7ede0] text-white'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon size={20} />
              <span className="text-sm font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-6 space-y-2">
          <Link to="/">
            <Button variant="ghost" className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5">
              <House size={20} className="mr-3" />
              Voir le site
            </Button>
          </Link>
          <Button 
            variant="ghost" 
            onClick={logout}
            className="w-full justify-start text-gray-400 hover:text-[#FF3B30] hover:bg-red-500/10"
          >
            <SignOut size={20} className="mr-3" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#000000] text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png" alt="Calvi Fishing" className="h-8 w-8 object-contain rounded-full" />
            <span className="font-bold font-['Playfair_Display']">CALVI FISHING</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white">{mobileMenuOpen ? <X size={24} /> : <List size={24} />}</Button>
        </div>
        {mobileMenuOpen && (
          <nav className="mt-3 pb-3 border-t border-white/10 pt-3 space-y-1">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg ${item.path === '/admin/analytics' ? 'bg-[#b7ede0] text-[#000000] font-semibold' : 'text-gray-400'}`}>
                <item.icon size={18} /><span className="text-sm">{item.label}</span>
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-[#000000] font-['Playfair_Display']">
            Analytics & Performance
          </h1>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les données</SelectItem>
              <SelectItem value="year">Cette année</SelectItem>
              <SelectItem value="month">Ce mois</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats Overview */}
        {analytics && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="stat-card bg-gradient-to-br from-[#b7ede0]/10 to-transparent">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#b7ede0] flex items-center justify-center">
                      <CurrencyEur size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-[#4B5563]">Revenus totaux</p>
                      <p className="text-2xl font-bold text-[#000000]">{analytics.total_revenue.toFixed(0)}€</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="stat-card bg-gradient-to-br from-[#10B981]/10 to-transparent">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#10B981] flex items-center justify-center">
                      <CalendarDots size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-[#4B5563]">Total réservations</p>
                      <p className="text-2xl font-bold text-[#000000]">{analytics.total_bookings}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="stat-card bg-gradient-to-br from-[#8B5CF6]/10 to-transparent">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#8B5CF6] flex items-center justify-center">
                      <Users size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-[#4B5563]">Total participants</p>
                      <p className="text-2xl font-bold text-[#000000]">{analytics.total_participants}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="stat-card bg-gradient-to-br from-[#FF3B30]/10 to-transparent">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#FF3B30] flex items-center justify-center">
                      <Prohibit size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-[#4B5563]">Taux annulation</p>
                      <p className="text-2xl font-bold text-[#000000]">{analytics.cancellation_rate}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 mb-8">
              {/* Revenue by Trip */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-['Playfair_Display']">Revenus par sortie</CardTitle>
                </CardHeader>
                <CardContent>
                  {tripChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={tripChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value, name) => [
                            name === 'revenue' ? `${value}€` : value,
                            name === 'revenue' ? 'Revenus' : 'Réservations'
                          ]}
                        />
                        <Bar dataKey="revenue" fill="#b7ede0" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-[#4B5563] py-10">Aucune donnée disponible</p>
                  )}
                </CardContent>
              </Card>

              {/* Bookings by Trip (Pie) */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-['Playfair_Display']">Répartition des réservations</CardTitle>
                </CardHeader>
                <CardContent>
                  {tripChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={tripChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="reservations"
                        >
                          {tripChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-[#4B5563] py-10">Aucune donnée disponible</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Monthly Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="font-['Playfair_Display']">Évolution mensuelle</CardTitle>
              </CardHeader>
              <CardContent>
                {monthlyChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlyChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value, name) => [
                          name === 'revenue' ? `${value}€` : value,
                          name === 'revenue' ? 'Revenus' : 'Réservations'
                        ]}
                      />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="reservations" 
                        stroke="#10B981" 
                        strokeWidth={2}
                        dot={{ fill: '#10B981' }}
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#b7ede0" 
                        strokeWidth={2}
                        dot={{ fill: '#b7ede0' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-[#4B5563] py-10">Aucune donnée disponible</p>
                )}
              </CardContent>
            </Card>

            {/* Trip Details Table */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle className="font-['Playfair_Display']">Détail par sortie</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-[#4B5563]">Sortie</th>
                        <th className="text-right py-3 px-4 font-medium text-[#4B5563]">Réservations</th>
                        <th className="text-right py-3 px-4 font-medium text-[#4B5563]">Revenus</th>
                        <th className="text-right py-3 px-4 font-medium text-[#4B5563]">Panier moyen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(analytics.bookings_by_trip).map(([id, data]) => (
                        <tr key={id} className="border-b">
                          <td className="py-3 px-4 font-medium">{data.name}</td>
                          <td className="py-3 px-4 text-right">{data.count}</td>
                          <td className="py-3 px-4 text-right font-medium">{data.revenue.toFixed(0)}€</td>
                          <td className="py-3 px-4 text-right text-[#4B5563]">
                            {data.count > 0 ? (data.revenue / data.count).toFixed(0) : 0}€
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminAnalyticsPage;
